import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Statistic,
  Card,
  Progress,
  Icon,
  Message,
  Label,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders } from '../../api';

export default function ContributorDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const res = await fetch(
          `${getApiPath()}/@polyglot/contributors/me`,
          { headers: getAuthHeaders() },
        );
        if (res.status === 404) {
          setError('no_profile');
          setLoading(false);
          return;
        }
        if (res.status === 401) {
          window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
          return;
        }
        if (!res.ok) {
          throw new Error('Failed to load dashboard');
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    }
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment loading placeholder style={{ minHeight: '200px' }} />
      </Container>
    );
  }

  if (error === 'no_profile') {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Helmet title="Become a Contributor" />

        <div className="polyglot-page-header">
          <h1>Become a Contributor</h1>
          <p className="subtitle">Help improve translations across 71 languages</p>
        </div>

        <div style={{ clear: 'both' }} />

        <Segment style={{ fontSize: '1.05em', lineHeight: '1.7' }}>
          <p>
            The Polyglot Translation Project manages translations for the
            Plone/Volto ecosystem — 330,000+ translations across 71 languages,
            with full audit trails and quality workflows.
          </p>
          <p>
            We welcome contributors who can help improve translation quality
            for languages they know well. All contributions are dedicated to
            the <strong>public domain</strong> (CC0) — free for everyone, forever.
            Contributors are <strong>held harmless</strong> for translations used
            within the LJUSA TIDER platform.
          </p>
        </Segment>

        <Segment>
          <Header as="h3">What Contributors Do</Header>
          <Card.Group itemsPerRow={3} stackable>
            <Card>
              <Card.Content>
                <Icon name="search" size="big" style={{ float: 'right', color: 'var(--quanta-sapphire, #0b78d0)' }} />
                <Card.Header>Browse &amp; Fix</Card.Header>
                <Card.Description>
                  Find missing or incorrect translations for your assigned languages
                  and submit corrections with full context.
                </Card.Description>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <Icon name="check circle" size="big" style={{ float: 'right', color: 'var(--quanta-neon, #3da72a)' }} />
                <Card.Header>Review</Card.Header>
                <Card.Description>
                  Review translations by other contributors. Check for accuracy,
                  completeness, and natural expression in the target language.
                </Card.Description>
              </Card.Content>
            </Card>
            <Card>
              <Card.Content>
                <Icon name="flag" size="big" style={{ float: 'right', color: 'var(--quanta-gold, #b48f09)' }} />
                <Card.Header>Flag Issues</Card.Header>
                <Card.Description>
                  Spot an ambiguous source string or a terminology conflict?
                  Flag it for discussion in the review queue.
                </Card.Description>
              </Card.Content>
            </Card>
          </Card.Group>
        </Segment>

        <Segment>
          <Header as="h3">What You Get</Header>
          <Statistic.Group widths="four" size="mini">
            <Statistic>
              <Statistic.Value>71</Statistic.Value>
              <Statistic.Label>Languages</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>330K+</Statistic.Value>
              <Statistic.Label>Translations</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>CC0</Statistic.Value>
              <Statistic.Label>Public Domain</Statistic.Label>
            </Statistic>
            <Statistic>
              <Statistic.Value>0</Statistic.Value>
              <Statistic.Label>Your Liability</Statistic.Label>
            </Statistic>
          </Statistic.Group>
        </Segment>

        <Segment textAlign="center" style={{ paddingTop: '1.5em', paddingBottom: '1.5em' }}>
          <p style={{ color: '#888', marginBottom: '1.5em' }}>
            Once registered, your personal dashboard will show your assigned
            languages, coverage progress, and correction stats.
          </p>
          <Link to="/polyglot/apply" className="ui large primary button">
            <Icon name="hand paper" /> Apply to Contribute
          </Link>
          <div style={{ marginTop: '1em' }}>
            <Link to="/policies/AGR-TRN-001" style={{ color: '#888' }}>
              Read the Contributors Agreement first
            </Link>
          </div>
        </Segment>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Message error>{error}</Message>
      </Container>
    );
  }

  if (!data) return null;

  const memberDays = Math.floor(
    (Date.now() - new Date(data.member_since).getTime()) / 86400000,
  );

  return (
    <Container style={{ paddingTop: '2em' }}>
      <Helmet title="Contributor Dashboard" />

      {/* Welcome */}
      <Segment>
        <Header as="h1">
          <Icon name="hand peace" />
          <Header.Content>
            Welcome back, {data.fullname.split(' ')[0]}
            <Header.Subheader>Polyglot Translation Contributor</Header.Subheader>
          </Header.Content>
        </Header>
      </Segment>

      {/* Summary Statistics */}
      <Segment>
        <Statistic.Group widths="three" size="small">
          <Statistic>
            <Statistic.Value>{data.total_contributions}</Statistic.Value>
            <Statistic.Label>Contributions</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{data.languages.length}</Statistic.Value>
            <Statistic.Label>Languages Assigned</Statistic.Label>
          </Statistic>
          <Statistic>
            <Statistic.Value>{memberDays < 1 ? '< 1' : memberDays}</Statistic.Value>
            <Statistic.Label>Days as Contributor</Statistic.Label>
          </Statistic>
        </Statistic.Group>
      </Segment>

      {/* Per-Language Cards */}
      <Header as="h2">Your Languages</Header>
      <Card.Group itemsPerRow={2} stackable>
        {data.languages.map((lang) => {
          // Support both old (single coverage) and new (platforms array) format
          const platforms = lang.platforms || (lang.coverage ? [{
            platform: 'nick',
            total: lang.coverage.total,
            translated: lang.coverage.translated,
            percentage: lang.coverage.percentage,
          }] : []);
          const totalGaps = platforms.reduce((sum, p) => sum + ((p.total || 0) - (p.translated || 0)), 0);
          return (
            <Card key={lang.code}>
              <Card.Content>
                <Card.Header>
                  {lang.name || lang.code}
                  {lang.native_name && lang.native_name !== lang.name && (
                    <span style={{ fontWeight: 'normal', color: '#888', marginLeft: '0.5em' }}>
                      {lang.native_name}
                    </span>
                  )}
                </Card.Header>
                <Card.Meta>
                  <Label size="small" color="blue">{lang.code}</Label>
                </Card.Meta>
              </Card.Content>
              <Card.Content>
                {platforms.map((p) => {
                  const pct = p.percentage || 0;
                  return (
                    <div key={p.platform} style={{ marginBottom: '0.6em' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85em' }}>
                        <strong>{p.platform}</strong>
                        <span style={{ color: '#888' }}>{p.translated || 0} / {p.total || 0}</span>
                      </div>
                      <Progress
                        percent={Math.round(pct * 10) / 10}
                        progress
                        color={pct >= 100 ? 'green' : pct >= 90 ? 'olive' : 'yellow'}
                        size="tiny"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  );
                })}
                <Statistic.Group size="mini" widths="two" style={{ marginTop: '0.5em' }}>
                  <Statistic>
                    <Statistic.Value>{lang.corrections_submitted}</Statistic.Value>
                    <Statistic.Label>Corrections</Statistic.Label>
                  </Statistic>
                  <Statistic color={totalGaps > 0 ? 'red' : 'green'}>
                    <Statistic.Value>{totalGaps}</Statistic.Value>
                    <Statistic.Label>Gaps</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Card.Content>
              <Card.Content extra>
                <div className="ui two buttons">
                  <Link
                    to={`/polyglot/review?language=${lang.code}`}
                    className="ui basic blue button"
                  >
                    <Icon name="pencil" /> Start Reviewing
                  </Link>
                  <Link
                    to={`/polyglot/languages/${lang.code}`}
                    className="ui basic button"
                  >
                    <Icon name="chart bar" /> Details
                  </Link>
                </div>
              </Card.Content>
            </Card>
          );
        })}
      </Card.Group>

      {data.languages.length === 0 && (
        <Message info>
          <Message.Header>No Languages Assigned</Message.Header>
          <p>
            Your administrator has not yet assigned any languages to your
            profile. Please contact them to get started.
          </p>
        </Message>
      )}
    </Container>
  );
}
